import React, { useState, useEffect, useRef, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { testimonialService } from '../../services/testimonialService.js';
import { Plus, MessageSquareQuote } from "lucide-react";
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';


const TestimonialList = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const [imageErrors, setImageErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTestimonials = useCallback(async () => {
    try {
      setLoading(true);
      // Request with proper pagination and include_image=true
      const response = await testimonialService.getAllTestimonials(
        currentPage, 
        perPage, 
        false, 
        true
      );
      setTestimonials((prev) => mergeLoadedPage(currentPage, response.data.data || [], prev));
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      Alert.error('Failed to load testimonials');
      console.error(err);
      if (currentPage <= 1) setTestimonials([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage]);

  usePaginatedListFetch({
    fetchPage: fetchTestimonials,
    setCurrentPage,
    currentPage,
    resetPageDeps: [],
  });

  const handleDelete = async (testimonialId, testimonialName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete the testimonial from "${testimonialName}"? This action cannot be undone.`,
      'Delete Testimonial'
    );
    
    if (confirmed) {
      try {
        await testimonialService.deleteTestimonial(testimonialId);
        Alert.success('Testimonial deleted successfully!');
        setActiveDropdown(null);
        fetchTestimonials();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete testimonial');
      }
    }
  };

  const toggleDropdown = (testimonialId) => {
    setActiveDropdown(activeDropdown === testimonialId ? null : testimonialId);
  };

  const renderStars = (rating) => {
    return '⭐'.repeat(rating);
  };

  const getImageSrc = (testimonial) => {
    if (testimonial.image_base64) {
      return `data:image/jpeg;base64,${testimonial.image_base64}`;
    }
    return null;
  };

  const renderProfileImage = (testimonial) => {
    const imageSrc = getImageSrc(testimonial);
    const hasError = imageErrors[testimonial.id];
    
    if (imageSrc && !hasError) {
      return (
        <img 
          src={imageSrc} 
          alt={testimonial.full_name}
          className="w-full h-full object-cover"
          onError={() => setImageErrors(prev => ({ ...prev, [testimonial.id]: true }))}
        />
      );
    }
    
    return <span className="text-2xl">👤</span>;
  };

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                <ListPageHeader
                  title="Testimonials"
                  icon={MessageSquareQuote}
                  titleClassName="text-secondary"
                  iconClassName="w-6 h-6 text-buttonBg shrink-0"
                  className="mb-4 sm:mb-6"
                >
                  <Button mobileIconOnly variant="primary" onClick={() => navigate('/testimonials/add')} icon={Plus}>
                    Add Testimonial
                  </Button>
                </ListPageHeader>

                {loading && currentPage === 1 ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                  </div>
                ) : testimonials.length === 0 ? (
                  <div className="p-8 text-center text-gray-600">No testimonials found</div>
                ) : (
                  <>
                    {/* Table - Desktop */}
                    <div className="hidden lg:block bg-white rounded-xl shadow-sm">
                      <div className="w-full bg-white rounded-xl overflow-visible">
                        <div className="w-full" onClick={() => setActiveDropdown(null)}>
                        <table className="w-full table-fixed border-collapse">
                          <thead>
                            <tr className="bg-primary text-tertiary">
                                <th className="px-6 py-4 text-left text-sm font-semibold w-[8%]">IMAGE</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold w-[18%]">CUSTOMER NAME</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold w-[15%]">LOCATION</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold w-[10%]">RATING</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold w-[10%]">STATUS</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold w-[14%]">CREATED DATE</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold w-[10%]">ACTIONS</th>
                              </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-200">
                              {testimonials.map((testimonial) => (
                                <tr
                                  key={testimonial.id}
                                  className="hover:bg-gray-50 transition-colors relative"
                                >
                                  {/* IMAGE */}
                                  <td className="px-6 py-4">
                                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                                      {renderProfileImage(testimonial)}
                                    </div>
                                  </td>

                                  {/* NAME */}
                                  <td className="px-6 py-4 truncate">
                                    <button
                                      onClick={() => navigate(`/testimonials/view/${testimonial.id}`)}
                                      className={listIdLinkClass}
                                    >
                                      {testimonial.full_name}
                                    </button>
                                  </td>

                                  {/* LOCATION */}
                                  <td className="px-6 py-4 text-gray-700 truncate">
                                    {testimonial.location}
                                  </td>

                                  {/* RATING */}
                                  <td className="px-6 py-4 text-yellow-500">
                                    {renderStars(testimonial.rating)}
                                  </td>

                                  {/* STATUS */}
                                  <td className="px-6 py-4">
                                    <span
                                      className={`px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center ${
                                        testimonial.is_active
                                          ? "bg-green-100 text-green-800"
                                          : "bg-gray-100 text-gray-800"
                                      }`}
                                    >
                                      {testimonial.is_active ? "Active" : "Inactive"}
                                    </span>
                                  </td>

                                  {/* CREATED DATE */}
                                  <td className="px-6 py-4 text-gray-700">
                                    {new Date(testimonial.created_at).toLocaleDateString("en-IN", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </td>

                                  {/* ACTIONS */}
                                  <td className="px-6 py-4 relative">
                                    <RowActionsMenu
                                      isOpen={activeDropdown === testimonial.id}
                                      onToggle={() => toggleDropdown(testimonial.id)}
                                      onClose={() => setActiveDropdown(null)}
                                      menuRef={dropdownRef}
                                      onView={() => navigate(`/testimonials/view/${testimonial.id}`)}
                                      onEdit={() => navigate(`/testimonials/edit/${testimonial.id}`)}
                                      onDelete={() => handleDelete(testimonial.id, testimonial.full_name)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                    </div>

                    {/* Cards - Mobile */}
                    <div className="lg:hidden space-y-3">
                      {testimonials.map((testimonial) => (
                        <div key={testimonial.id} className="bg-white rounded-xl shadow-sm p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-3 flex-1">
                              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {renderProfileImage(testimonial)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <button
                                  onClick={() => navigate(`/testimonials/view/${testimonial.id}`)}
                                  className={`${listIdLinkMobileClass} text-left block truncate`}
                                >
                                  {testimonial.full_name}
                                </button>
                                <p className="text-sm text-gray-600 mt-1 truncate">{testimonial.location}</p>
                              </div>
                            </div>
                            <RowActionsMenu
                              isOpen={activeDropdown === testimonial.id}
                              onToggle={() => toggleDropdown(testimonial.id)}
                              onClose={() => setActiveDropdown(null)}
                              menuRef={dropdownRef}
                              onView={() => navigate(`/testimonials/view/${testimonial.id}`)}
                              onEdit={() => navigate(`/testimonials/edit/${testimonial.id}`)}
                              onDelete={() => handleDelete(testimonial.id, testimonial.full_name)}
                              className="relative flex-shrink-0 ml-2"
                            />
                          </div>

                          <div className="flex items-center justify-between mb-3">
                            <span className="text-yellow-500 text-sm">{renderStars(testimonial.rating)}</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              testimonial.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {testimonial.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>

                          <div className="text-xs text-gray-500">
                            Created: {new Date(testimonial.created_at).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>

                        </div>
                      ))}

                    </div>

                    <ListPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      loading={loading}
                      onNext={() => setCurrentPage((page) => page + 1)}
                    />
                  </>
                )}
              </div>
    </DashboardLayout>
  );
};

export default TestimonialList;